import { CSSProperties, useState, useEffect } from 'react';
import useCampaignBuilder, { TargetedGeographyBucket } from 'Hooks/useCampaignBuilder';
import { clsx, makeKey, styx } from 'modules/Utils';
import debounce from 'lodash/debounce';
import Sty from 'modules/Sty';
import Colors from 'modules/Colors';
import { FBLocation } from 'modules/Types';
import CardSymphony from 'elements/CardSymphony';
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import TagSymphony from 'elements/TagSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Banner from 'elements/Banner';
import DebugBox from 'components/shareable/DebugBox';
import useBrand from 'Hooks/useBrand';
import { getAvailableTargetingLocations_api } from 'api/tiktokApi';
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA';

interface TargetingCampaignView_GEOGRAPHYProps {
    className: string;
    style?: CSSProperties;
    platform: 'tiktok' | 'facebook'
    hideTitle?: boolean
}

const TargetingCampaignView_GEOGRAPHY = ({
    className,
    style,
    platform = 'facebook',
    hideTitle = false
}: TargetingCampaignView_GEOGRAPHYProps) => {
    const {
        searchGeographies,
        geographyBuckets,
        setGeographyBuckets,
        setShowEditGeographyBucketsBanner,
        getTotalBudget,
        validateGeographyBuckets,
        isGeographyBucketsObjectValid,
    } = useCampaignBuilder();

    const { currentBrand, isProBrand } = useBrand();

    const { isValid: canAddMultipleLocations } = getTotalBudget();

    const [isEditable, setIsEditable] = useState<boolean>(true);
    // Search results state
    const [searchResultGeographies, setSearchResultGeographies] = useState<FBLocation[]>([]);
    const [geographyBucketSearchTerms, setGeographyBucketSearchTerms] = useState<string[]>([]);

    useEffect(() => {
        if (geographyBuckets.length > geographyBucketSearchTerms.length) {
            const emptySearchTerms = Array(geographyBuckets.length).fill('');
            setGeographyBucketSearchTerms(emptySearchTerms);
        }
        validateGeographyBuckets();
    }, [geographyBuckets]);

    const handleFetchGeographies = async (query: string) => {
        if (platform === 'tiktok') {
            try {

                if (!currentBrand?.id) {
                    console.error('No current brand found for TikTok geography search');
                    return;
                }

                const tiktokLocations = await getAvailableTargetingLocations_api(currentBrand.id, query);

                if (tiktokLocations && tiktokLocations.length > 0) {
                    // Transform TikTokTargetingGeography to FBLocation format
                    const transformedLocations: FBLocation[] = tiktokLocations.map(location => ({
                        key: location.id,
                        name: location.name,
                        // Map region_level to appropriate location type
                        type: location.region_level === 'COUNTRY' ?
                            'country'
                            :
                            location.region_level === 'PROVINCE' || location.region_level === 'REGION' ? 'region' : 'city',
                        country_code: location.region_code || '',
                        country_name: location.parent_name || location.name,
                        region: location.parent_name || '',
                        region_id: 0, // Default since TikTok doesn't provide this
                        supports_city: Boolean(location.next_level_ids?.length)
                    }));

                    setSearchResultGeographies(transformedLocations);
                } else {
                    setSearchResultGeographies([]);
                }
            } catch (error) {
                console.error('Error fetching TikTok geographies:', error);
                setSearchResultGeographies([]);
            }
        } else {
            const results = await searchGeographies(query, 'facebook');
            setSearchResultGeographies(results as FBLocation[]);
        }
    }

    const handleDisplaySearchedGeography = (geography: FBLocation): SelectOptionItem => {
        const {
            country_code,
            country_name,
            region,
            name,
            key,
            type: locationType
        } = geography;

        let locationValue = '';
        let locationTitle = '';
        let locationDescription = '';
        let object = {};

        switch (locationType) {
            case 'country':
                locationValue = key
                locationTitle = `${country_name}`
                locationDescription = `COUNTRY - Target the entire country of ${country_name}`
                object = geography
                break
            case 'region':
                locationValue = key
                locationTitle = `${name}${region ? ` (${region})` : ''}`
                let regionValue = country_code === 'US' ? 'state' : 'region'
                locationDescription = `REGION - Target the entire ${regionValue} of ${name}`
                object = geography
                break
            case 'city':
                locationValue = key
                locationTitle = `${name}, ${region} (${country_name})`
                locationDescription = `CITY - Target audiences in the city of ${name}`
                object = geography
                break
        }

        return {
            value: locationValue,
            title: locationTitle,
            description: locationDescription,
            object: object
        }
    }

    const handleSelectGeographyFromList = (geographyOption: SelectOptionItem, bucket: TargetedGeographyBucket) => {
        const { object } = geographyOption;
        const exists = bucket.geographies.some(g => g.id === object.key);
        if (exists) {
            return;
        }

        const updatedBuckets = geographyBuckets.map(b => {
            if (b.id === bucket.id) {
                return {
                    ...b,
                    geographies: [...b.geographies, {
                        id: object.key,
                        name: object.name,
                        label: `${object.name} (${object.type})`,
                        type: object.type,
                        radius: 0,
                        country_code: object.country_code,
                        country_name: object.country_name,
                        region: object.region,
                        region_id: object.region_id,
                        supports_city: object.supports_city
                    }]
                };
            }
            return b;
        });

        setGeographyBuckets(updatedBuckets);
        setShowEditGeographyBucketsBanner(false);

        // Clear the search term for the selected bucket
        const bucketIndex = geographyBuckets.findIndex(b => b.id === bucket.id);
        const updatedSearchTerms = [...geographyBucketSearchTerms];
        updatedSearchTerms[bucketIndex] = '';
        setGeographyBucketSearchTerms(updatedSearchTerms);
    }

    /** Updates a single bucket's budget */
    const updateBucketBudgetPercentage = (index: number, increment: boolean) => {
        // If there are exactly 2 buckets, we need to keep them totaling 100%
        if (geographyBuckets.length === 2) {
            const otherIndex = index === 0 ? 1 : 0;
            const changeAmount = increment ? 5 : -5;

            // Only proceed if the change would still keep both buckets within valid ranges
            let newPercentage = geographyBuckets[index].percentage + changeAmount;
            let otherNewPercentage = geographyBuckets[otherIndex].percentage - changeAmount;

            // Ensure percentages stay within valid ranges
            if (newPercentage < 0 || newPercentage > 100 || otherNewPercentage < 0 || otherNewPercentage > 100) {
                return; // Don't allow the change if it would make either bucket invalid
            }

            // Update both buckets to maintain 100% total
            const updatedBuckets = [...geographyBuckets];
            updatedBuckets[index] = {
                ...updatedBuckets[index],
                percentage: newPercentage
            };
            updatedBuckets[otherIndex] = {
                ...updatedBuckets[otherIndex],
                percentage: otherNewPercentage
            };

            setGeographyBuckets(updatedBuckets);
        } else {
            // Original behavior for single bucket or more than 2 buckets
            const bucket = geographyBuckets[index];
            let newPercentage = bucket.percentage + (increment ? 5 : -5)
            if (newPercentage < 0) {
                newPercentage = 0;
            }
            if (newPercentage > 100) {
                newPercentage = 100;
            }
            const updatedBuckets = geographyBuckets.map((bucket, i) =>
                i === index ?
                    {
                        ...bucket,
                        percentage: newPercentage
                    }
                    :
                    bucket
            );
            setGeographyBuckets(updatedBuckets);
        }
    }

    const renderBucketNameInput = (bucket: TargetedGeographyBucket, index: number) => {
        if (isEditable) {
            return (
                <InputSymphony
                    label="Audience Name"
                    className="geographyBucketNameInput"
                    value={bucket.name}
                    onChange={(valueText) => {
                        if (geographyBuckets.length > 0) {
                            const updatedBuckets = geographyBuckets.map((b, i) =>
                                i === index ? { ...b, name: valueText } : b
                            );
                            setGeographyBuckets(updatedBuckets);
                        }
                    }}
                    placeholder="Enter bucket name e.g. Primary Markets"
                />
            )
        }

        return (
            <div className="bucketName" style={Sty.text16bold}>
                {bucket.name}
            </div>
        )
    }

    const renderDeleteBucketButton = (index: number) => {
        if (isEditable && geographyBuckets.length > 1) {
            return (
                <Icon
                    className="removeBucketButton"
                    icon="trash"
                    color={Colors.greyDark}
                    size={28}
                    onClick={() => {
                        const bucketId = geographyBuckets[index].id;
                        const bucketIndex = geographyBucketSearchTerms.findIndex(term => term.includes(bucketId.toString()));
                        // Create deep copy of buckets array with all properties
                        const copiedBuckets = geographyBuckets.map(bucket => ({
                            ...bucket,
                            geographies: [...bucket.geographies]
                        }));

                        const updatedBuckets = copiedBuckets.filter((_, i) => i !== index);
                        if (updatedBuckets.length === 1) {
                            updatedBuckets[0] = {
                                ...updatedBuckets[0],
                                percentage: 100
                            };
                        }
                        setGeographyBuckets(updatedBuckets);
                        setGeographyBucketSearchTerms([...geographyBucketSearchTerms.slice(0, bucketIndex), ...geographyBucketSearchTerms.slice(bucketIndex + 1)]);
                    }}
                />
            )
        }

        return null
    }

    const renderLocationTargetingInput = (bucket: TargetedGeographyBucket, index: number) => {
        if (isEditable) {
            return (
                <InputSymphony
                    label="Search for locations to add:"
                    className="geographyBucketLocationAutocomplete"
                    value={geographyBucketSearchTerms?.[index] || ''}
                    onChange={(valueText) => {
                        if (geographyBucketSearchTerms.length > 0) {
                            const updatedBucketSearchTerms = [...geographyBucketSearchTerms];
                            updatedBucketSearchTerms[index] = valueText;
                            setGeographyBucketSearchTerms(updatedBucketSearchTerms);
                        }
                    }}
                    placeholder="Search for cities, states, and countries e.g. United States"
                    isSelect
                    fetchSelectedOptions={async () => {
                        if (geographyBucketSearchTerms.length > 0) {
                            const searchTerm = geographyBucketSearchTerms[index];
                            handleFetchGeographies(searchTerm);
                        }
                        return []
                    }}
                    options={searchResultGeographies.map((geography) => handleDisplaySearchedGeography(geography))}
                    onOptionSelect={(option) => handleSelectGeographyFromList(option, bucket)}
                />
            )
        }

        return (
            <div className="geographyBucketSearchTerm" style={Sty.text16bold}>
                {geographyBucketSearchTerms[index]}
            </div>
        )
    }

    const renderBudgetControls = (percentage: number, index: number) => {
        // If not editable or (we have only one bucket and can't add more locations due to budget)
        if (!isEditable || (geographyBuckets.length === 1 && !canAddMultipleLocations)) {
            return (
                <div
                    className="budgetControlsReadOnly"
                    style={styx(
                        Sty.text14grey,
                        sty.budgetControlsReadOnly
                    )}
                >
                    {percentage}%
                </div>
            )
        }

        return (
            <div className="budgetControls" style={sty.budgetControls}>
                <Icon
                    className="decrementBudgetIcon"
                    icon="remove"
                    color={Colors.greyDark}
                    size={24}
                    onClick={() => updateBucketBudgetPercentage(index, false)}
                />
                <div className="budgetPercentage" style={Sty.text14grey}>
                    {percentage}%
                </div>
                <Icon
                    className="incrementBudgetIcon"
                    icon="addCircle"
                    color={Colors.greyDark}
                    size={24}
                    onClick={() => updateBucketBudgetPercentage(index, true)}
                />
            </div>
        )
    }

    const renderBucketTargetedLocations = (bucket: TargetedGeographyBucket, index: number) => {
        let geographyTargetTitle = 'This audience group will target the following locations:'

        if (isEditable) {
            geographyTargetTitle = ''
        }

        // Group geographies by type
        const groupedGeographies = {
            country: bucket.geographies.filter(geo => geo.type === 'country'),
            region: bucket.geographies.filter(geo => geo.type === 'region'),
            city: bucket.geographies.filter(geo => geo.type === 'city')
        };

        // Render geography tags by type
        const renderGeographyTypeSection = (geoType: 'country' | 'region' | 'city', geographyTypeTitle: string) => {
            const geographies = groupedGeographies[geoType];

            if (geographies.length === 0) {
                return null;
            }

            return (
                <div
                    className={`geographyTypeSection-${geoType}`}
                    style={{ marginBottom: 12 }}
                >
                    <div
                        className="geographyTypeSectionTitle"
                        style={{ ...Sty.text14bold, marginBottom: 4 }}
                    >
                        {geographyTypeTitle}
                    </div>
                    <div
                        className="tagsContainerInner"
                        style={sty.tagsContainer}
                    >
                        {geographies.map((location) => {
                            return (
                                <TagSymphony
                                    key={makeKey('geography-tag', location.id)}
                                    className="selectedGeographyTag"
                                    title={location.label || `${location.name} (${location.type})`}
                                    color="purple"
                                    onRemove={() => {
                                        const updatedBuckets = geographyBuckets.map((b, i) =>
                                            i === index ?
                                                {
                                                    ...b,
                                                    geographies: b.geographies.filter(l => l.id !== location.id)
                                                }
                                                :
                                                b
                                        );
                                        setGeographyBuckets(updatedBuckets);
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>
            );
        };

        return (
            <div className="bucketTargetedLocations" style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
                <div className="bucketTargetedLocationsTitle" style={Sty.text14grey}>
                    {geographyTargetTitle}
                </div>
                {isEditable &&
                    renderLocationTargetingInput(bucket, index)
                }

                <Banner
                    color="purple"
                    className="geographyTagsSectionContainer"
                    style={{
                        marginTop: 8
                    }}
                >
                    <div
                        className="geographyTypeSectionTargetedLocationsTitle"
                        style={{
                            ...Sty.text16bold,
                            marginBottom: 12
                        }}
                    >
                        Targeted Locations
                    </div>

                    {renderGeographyTypeSection('country', 'Countries:')}
                    {renderGeographyTypeSection('region', 'Regions:')}
                    {renderGeographyTypeSection('city', 'Cities:')}

                    {bucket.geographies.length === 0 &&
                        <div style={Sty.text14grey}>
                            No locations selected yet. Search and add locations above.
                        </div>
                    }
                </Banner>
            </div>
        )
    }

    const renderAddMoreLocations = () => {
        if (platform === 'tiktok') {
            return null;
        }

        // Max of 2 buckets
        if (geographyBuckets.length >= 2) {
            return null
        }

        if (!isEditable) {
            return null
        }

        return (
            <div
                className="addMoreLocationsContainer"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    alignItems: 'center'
                }}
            >
                {!canAddMultipleLocations &&
                    <div
                        className="addMoreLocationsMinimumBudgetMessage"
                        style={Sty.text14purple}
                    >
                        To add more locations, your minimum campaign budget must be at least $100 total.
                    </div>
                }

                <ButtonSymphony
                    className="addLocationButton"
                    variant="outlined"
                    color="purple"
                    disabled={!canAddMultipleLocations || !isProBrand}
                    onClick={() => {
                        const newBucket = [
                            {
                                id: geographyBuckets.length,
                                name: `Location Targeting Set #${geographyBuckets.length + 1}`,
                                percentage: 5, // Start new bucket with 5% instead of 0%
                                geographies: []
                            },
                        ];

                        // If this will make 2 buckets total, adjust the first bucket's percentage
                        if (geographyBuckets.length === 1) {
                            const updatedBuckets = [
                                {
                                    ...geographyBuckets[0],
                                    percentage: 95 // First bucket gets 95% of the budget
                                },
                                ...newBucket
                            ];
                            setGeographyBuckets(updatedBuckets);
                        } else {
                            setGeographyBuckets([...geographyBuckets, ...newBucket]);
                        }

                        setGeographyBucketSearchTerms([...geographyBucketSearchTerms, '']);
                    }}
                >
                    Add More Locations
                </ButtonSymphony>

                {!isProBrand &&
                    <Banner
                        className="addMoreLocationsUpgradeToUnlockCTA"
                        style={{
                            width: '100%',
                            marginTop: 8
                        }}
                        color="purple"
                        title="Upgrade to Pro to test multiple locations."
                    >
                        <UpgradeToUnlockCTA
                            className="addMoreLocationsUpgradeToUnlockCTA"
                            requiresPaidPlan={true}
                            paywallText="With Pro, you can add multiple locations and split your budget across them."
                            buttonText="Upgrade to Pro"
                            upgradeSource="geography_targeting"
                        />
                    </Banner>
                }
            </div>
        )
    }

    const renderGeographyBuckets = () => {
        return (
            <div
                className="geographyBucketsSection"
                style={sty.geographyBucketsSection}
            >
                {geographyBuckets.map((bucket, index) => {
                    return (
                        <Banner
                            key={makeKey('geography-bucket', index)}
                            className="geographyBucketsSectionInner"
                            title={`Geography Audience #${index + 1}`}
                            color="white"
                            style={sty.geographyBucketsSectionInner}
                        >
                            <div className="bucketInputContainer" style={sty.bucketInputContainer}>
                                {/* bucket name input */}
                                {renderBucketNameInput(bucket, index)}
                                {renderDeleteBucketButton(index)}
                                {/* bucket percentage controls */}
                                {renderBudgetControls(bucket.percentage, index)}
                            </div>
                            {/* bucket location autocomplete and search input */}
                            {!isEditable &&
                                <div className="bucketDescription">
                                    This is based on your post content, and it's the right targeting for you! Includes audiences like:
                                </div>
                            }

                            {renderBucketTargetedLocations(bucket, index)}
                        </Banner>
                    )
                })}

                {!isGeographyBucketsObjectValid &&
                    <div
                        className="errorGeographyBudgetMessage"
                        style={sty.errorGeographyBudgetMessage}
                    >
                        Budget allocations must add up to 100%, be greater than 0, and at least one location must be selected.
                    </div>
                }

                {renderAddMoreLocations()}
            </div>
        );
    };

    const renderAutomaticaTargetingBanner = () => {
        if (isEditable) {
            return null
        }
        return (
            <Banner
                className="automaticTargetingBanner"
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <Icon
                    className="automaticaTargetingBannerIcon"
                    icon="robot"
                />
                <div style={Sty.text16}>
                    Symphony will automatically target your <span style={{ color: Colors.purple }}>marketing in primary countries that help you build a touring fanbase</span> and <span style={{ color: Colors.purple }}>global countries that help grow your monthly listeners more easily</span>.
                </div>
                <ButtonSymphony
                    className="automaticTargetingBannerEditButton"
                    onClick={() => setIsEditable(true)}
                    variant="outlined"
                >
                    Edit
                </ButtonSymphony>
            </Banner>
        )
    }

    // render()
    return (
        <CardSymphony
            className={clsx('targetingCampaignView_GEOGRAPHYComponent', className)}
            style={style}
            title={hideTitle ? '' : 'Geographies'}
            subtitle={hideTitle ? '' : 'Expand your audience to global markets.'}
            size="small"
            isPadding16={!hideTitle}
            isNoPadding={hideTitle}

        >

            {renderGeographyBuckets()}
        </CardSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    geographiesSection: {
        width: '100%'
    },
    infoSection: {
        padding: '0 40px',
        width: '100%'
    },
    starsSectionWrapper: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        marginTop: 4,
        alignSelf: 'flex-start',
    },
    geographySimpleSection: {
        width: '100%'
    },
    geographyBucketsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        width: '100%'
    },
    geographyBucketsSectionInner: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflow: 'visible',
        width: '100%',
        borderRadius: 8,
        border: Sty.border,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 8,
        paddingRight: 8
    },
    optionContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    chipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 16,
    },
    selectedGeographiesContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 16,
    },
    bucketInputContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 8,
        flexShrink: 0
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 8
    },
    errorGeographyBudgetMessage: {
        color: Colors.red,
    },
    budgetControls: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 4,
        paddingBottom: 4,
        borderRadius: 100,
        backgroundColor: Colors.greyLighter,
    },
    decrementIncrementBudgetIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 100,
        backgroundColor: '#ddd',
        cursor: 'pointer'
    },
    budgetControlsReadOnly: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 4,
        paddingBottom: 4,
        borderRadius: 100,
        backgroundColor: Colors.greyLighter,
        color: Colors.greyDark,
    },
};

export default TargetingCampaignView_GEOGRAPHY;